import * as React from 'react';
import moment from 'moment';
import './TournamentCommunicationsSidebar.scss';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import {
  createTournamentAnnouncement,
  createTournamentPrivateConversation,
  createTournamentGroupConversation,
  deleteTournamentAnnouncement,
  assignTournamentConversation,
  deleteTournamentMessage,
  downloadTournamentSafeguardingReport,
  editTournamentMessage,
  getTournamentConversationAudit,
  getTournamentCommunicationAudiences,
  getTournamentCommunicationConfig,
  getTournamentConversationMessages,
  getTournamentConversations,
  getTournamentMentionCandidates,
  markTournamentConversationRead,
  renameTournamentConversation,
  reportTournamentMessage,
  resolveTournamentMessageReport,
  sendTournamentConversationMessage,
  updateTournamentConversationStatus
} from 'Src/helpers/service/adminSU/tournamentCommunications';

interface Props {
  user: AppUser;
  tournament: Tournament;
  organiserMode?: boolean;
  threadOnly?: boolean;
  initialConversationId?: string;
  participantUnionId?: string;
}

const idOf = value => String(value && (value.id || value._id));
const displayText = (value: any, fallback = ''): string => {
  let current = value;
  while (current && typeof current === 'object' && !Array.isArray(current) && Object.prototype.hasOwnProperty.call(current, 'items')) current = current.items;
  return typeof current === 'string' || typeof current === 'number' ? String(current) : fallback;
};
const initials = (name: any) => displayText(name, 'Staff').trim().split(/\s+/).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase() || 'S';

export const TournamentCommunications: React.FC<Props> = ({ user: originalUser, tournament, organiserMode = true, threadOnly = false, initialConversationId, participantUnionId }) => {
  const user = React.useMemo(() => participantUnionId ? {...originalUser, tournamentCommunicationsUnionId: participantUnionId} : originalUser, [originalUser, participantUnionId]);
  const [conversations, setConversations] = React.useState<any[]>([]);
  const [audiences, setAudiences] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<any>();
  const [messages, setMessages] = React.useState<any[]>([]);
  const [text, setText] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<any>();
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [schoolId, setSchoolId] = React.useState('');
  const [announcementOpen, setAnnouncementOpen] = React.useState(false);
  const [groupOpen, setGroupOpen] = React.useState(false);
  const [group, setGroup] = React.useState({ title: '', audience: '', schoolIds: [] as string[] });
  const [announcement, setAnnouncement] = React.useState({ title: '', text: '', audience: 'PARTICIPANTS' });
  const [search, setSearch] = React.useState('');
  const [messageSearch, setMessageSearch] = React.useState('');
  const [mentions, setMentions] = React.useState<any[]>([]);
  const [mentionCandidates, setMentionCandidates] = React.useState<any[]>([]);
  const [mentionOpen, setMentionOpen] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState<any>();
  const [action, setAction] = React.useState<'EDIT' | 'DELETE' | 'REPORT'>();
  const [actionValue, setActionValue] = React.useState('');
  const [audit, setAudit] = React.useState<any[]>([]);
  const [auditOpen, setAuditOpen] = React.useState(false);
  const [accessConfig, setAccessConfig] = React.useState<any>();
  const [inboxFilter, setInboxFilter] = React.useState<'ALL' | 'UNREAD' | 'ASSIGNED' | 'CLOSED'>('ALL');
  const [typeFilter, setTypeFilter] = React.useState('ALL');
  const [newMenuOpen, setNewMenuOpen] = React.useState(false);
  const [privateOpen, setPrivateOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(threadOnly && window.innerWidth >= 1240);
  const [statusMenuOpen, setStatusMenuOpen] = React.useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
  const [messageSearchOpen, setMessageSearchOpen] = React.useState(false);
  const newMenuRef = React.useRef<HTMLDivElement>(null);
  const statusMenuRef = React.useRef<HTMLDivElement>(null);
  const composerRef = React.useRef<HTMLTextAreaElement>(null);
  const mentionControlRef = React.useRef<HTMLDivElement>(null);
  const messagesRef = React.useRef<HTMLDivElement>(null);
  const [expandedReceiptMessageId, setExpandedReceiptMessageId] = React.useState('');
  const tournamentId = String(tournament.id);
  const settings = accessConfig ||
    tournament.communications || {
      enabled: true,
      schoolInitiation: 'ALL_CONNECTED',
      announcementsEnabled: true,
      announcementAudiences: ['PARTICIPANTS', 'WAITING_LIST', 'INVITED', 'ALL_ENGAGED'],
      autoCloseDaysAfterEnd: 30
    };
  const allowedAnnouncementAudiences: string[] = settings.announcementsEnabled
    ? settings.announcementAudiences || []
    : [];
  const canCreateConversation = Boolean(settings.enabled && (organiserMode || settings.canInitiate));
  React.useEffect(() => {
    if (!allowedAnnouncementAudiences.length && announcementOpen) {
      setAnnouncementOpen(false);
      return;
    }
    if (
      allowedAnnouncementAudiences.length &&
      !allowedAnnouncementAudiences.includes(announcement.audience)
    ) {
      setAnnouncement(value => ({...value, audience: allowedAnnouncementAudiences[0]}));
    }
  }, [allowedAnnouncementAudiences.join('|'), announcement.audience, announcementOpen]);
  const schoolNames = React.useMemo(
    () => new Map(audiences.map(row => [String(row.school.id || row.school._id), row.school.name])),
    [audiences]
  );
  const visibleConversations = conversations.filter(item => {
    if (inboxFilter === 'UNREAD' && !item.unread) {
      return false;
    }
    if (inboxFilter === 'ASSIGNED' && String(item.assignedToUserId) !== String(user.userId)) {
      return false;
    }
    if (
      inboxFilter === 'CLOSED'
        ? !['CLOSED', 'ARCHIVED'].includes(item.status)
        : ['CLOSED', 'ARCHIVED'].includes(item.status)
    ) {
      return false;
    }
    return typeFilter === 'ALL' || item.type === typeFilter;
  });
  const unreadCount = conversations.filter(item => item.unread && !['CLOSED', 'ARCHIVED'].includes(item.status)).length;
  const assignedCount = conversations.filter(
    item => String(item.assignedToUserId) === String(user.userId) && !['CLOSED', 'ARCHIVED'].includes(item.status)
  ).length;
  const closedCount = conversations.filter(item => ['CLOSED', 'ARCHIVED'].includes(item.status)).length;
  const scrollMessagesToBottom = React.useCallback((smooth = false) => {
    window.requestAnimationFrame(() => {
      if (!messagesRef.current) {
        return;
      }
      messagesRef.current.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    });
  }, []);

  const conversationName = React.useCallback(
    (conversation: any) => {
      if (!organiserMode && conversation.type === 'PRIVATE') return `${displayText(conversation.organiserName, 'Tournament organiser')} organisers`;
      if (conversation.type === 'ANNOUNCEMENT' || conversation.type === 'GROUP') return displayText(conversation.title, 'Tournament conversation');
      if (conversation.title) return displayText(conversation.title, 'Tournament conversation');
      if (!organiserMode) return `${displayText(conversation.organiserName, 'Tournament organiser')} organisers`;
      return displayText(schoolNames.get(String(conversation.counterpartySchoolId)), 'School conversation');
    },
    [organiserMode, schoolNames]
  );

  const load = React.useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const [nextConversations, nextAudiences, nextConfig] = await Promise.all([
          getTournamentConversations(user, tournamentId, { search }),
          organiserMode ? getTournamentCommunicationAudiences(user, tournamentId) : Promise.resolve([]),
          getTournamentCommunicationConfig(user, tournamentId)
        ]);
        setConversations(nextConversations);
        setAudiences(nextAudiences);
        setAccessConfig(nextConfig);
        setSelected(current => current
          ? nextConversations.find(item => idOf(item) === idOf(current)) || current
          : nextConversations.find(item => idOf(item) === String(initialConversationId || '')) || nextConversations[0]);
        setError('');
      } catch (e) {
        if (!quiet) setError('Tournament chats could not be loaded. Please try again.');
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [user, tournamentId, organiserMode, search, initialConversationId]
  );

  React.useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  React.useEffect(() => {
    if (!newMenuOpen) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(event.target as Node)) setNewMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNewMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [newMenuOpen]);

  React.useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target as Node)) setStatusMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [statusMenuOpen]);

  React.useEffect(() => {
    if (!mentionOpen) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (mentionControlRef.current && !mentionControlRef.current.contains(event.target as Node) && event.target !== composerRef.current) setMentionOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMentionOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [mentionOpen]);

  React.useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }
    const conversationId = idOf(selected);
    getTournamentConversationMessages(user, tournamentId, conversationId, { search: messageSearch })
      .then(items => {
        setMessages(items);
        scrollMessagesToBottom();
      })
      .catch(() => setError('Messages could not be loaded. Please try again.'));
    markTournamentConversationRead(user, tournamentId, conversationId)
      .then(() => {
        setConversations(items =>
          items.map(item => (idOf(item) === conversationId ? { ...item, unread: false } : item))
        );
      })
      .catch(() => undefined);
    if (selected.type !== 'ANNOUNCEMENT') {
      getTournamentMentionCandidates(user, tournamentId, conversationId)
        .then(setMentionCandidates)
        .catch(() => setMentionCandidates([]));
    }
    const timer = window.setInterval(
      () =>
        getTournamentConversationMessages(user, tournamentId, conversationId, { search: messageSearch })
          .then(setMessages)
          .catch(() => undefined),
      10000
    );
    return () => window.clearInterval(timer);
  }, [selected, tournamentId, user, messageSearch, scrollMessagesToBottom]);

  const openSchoolChat = async () => {
    if (organiserMode && !schoolId) return;
    setBusy(true);
    try {
      const conversation = await createTournamentPrivateConversation(
        user,
        tournamentId,
        organiserMode ? schoolId : undefined
      );
      await load(true);
      setSelected({ ...conversation, canWrite: true, canManage: organiserMode });
      setSchoolId('');
      setError('');
    } catch (e) {
      setError('The private chat could not be opened.');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const value = text.trim();
    if (!value || !selected || busy) return;
    setBusy(true);
    try {
      await sendTournamentConversationMessage(
        user,
        tournamentId,
        idOf(selected),
        value,
        replyTo ? idOf(replyTo) : undefined,
        mentions
      );
      setText('');
      setReplyTo(undefined);
      setMentions([]);
      setMessages(await getTournamentConversationMessages(user, tournamentId, idOf(selected)));
      scrollMessagesToBottom(true);
      await load(true);
      setError('');
    } catch (e) {
      setError('Your message was not sent. Your text has been kept so you can try again.');
    } finally {
      setBusy(false);
    }
  };

  const assignToMe = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const updated = await assignTournamentConversation(
        user,
        tournamentId,
        idOf(selected),
        String(selected.assignedToUserId) !== String(user.userId)
      );
      setSelected({ ...selected, ...updated });
      await load(true);
    } catch (e) {
      setError('The conversation assignment could not be updated.');
    } finally {
      setBusy(false);
    }
  };

  const runMessageAction = async () => {
    if (!selected || !actionMessage || !action || busy) {
      return;
    }
    setBusy(true);
    try {
      let updated;
      if (action === 'EDIT') {
        updated = await editTournamentMessage(user, tournamentId, idOf(selected), idOf(actionMessage), actionValue);
      }
      if (action === 'DELETE') {
        updated = await deleteTournamentMessage(user, tournamentId, idOf(selected), idOf(actionMessage), actionValue);
      }
      if (action === 'REPORT') {
        updated = await reportTournamentMessage(user, tournamentId, idOf(selected), idOf(actionMessage), actionValue);
      }
      setMessages(items => items.map(item => (idOf(item) === idOf(actionMessage) ? { ...item, ...updated } : item)));
      setAction(undefined);
      setActionMessage(undefined);
      setActionValue('');
      await load(true);
    } catch (e) {
      setError('The message action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const resolveReport = async (message, resolution: 'KEEP' | 'REMOVE') => {
    setBusy(true);
    try {
      const updated = await resolveTournamentMessageReport(
        user,
        tournamentId,
        idOf(selected),
        idOf(message),
        resolution
      );
      setMessages(items => items.map(item => (idOf(item) === idOf(message) ? { ...item, ...updated } : item)));
      await load(true);
    } catch (e) {
      setError('The report could not be resolved.');
    } finally {
      setBusy(false);
    }
  };

  const showAudit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      setAudit(await getTournamentConversationAudit(user, tournamentId, idOf(selected)));
      setAuditOpen(true);
    } catch (e) {
      setError('The audit record could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (status: string) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const updated = await updateTournamentConversationStatus(user, tournamentId, idOf(selected), status);
      await load(true);
      setSelected(
        status === 'ARCHIVED'
          ? undefined
          : { ...selected, ...updated, canWrite: status === 'OPEN' || status === 'READ_ONLY', canManage: true }
      );
      setError('');
    } catch (e) {
      setError('The conversation status could not be changed.');
    } finally {
      setBusy(false);
    }
  };

  const saveTitle = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const updated = await renameTournamentConversation(user, tournamentId, idOf(selected), title);
      await load(true);
      setSelected({ ...selected, ...updated, canManage: true });
      setEditingTitle(false);
      setError('');
    } catch (e) {
      setError('The conversation name could not be changed.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!announcement.text.trim() || !allowedAnnouncementAudiences.includes(announcement.audience) || busy) return;
    setBusy(true);
    try {
      const conversation = await createTournamentAnnouncement(user, tournamentId, announcement);
      setAnnouncement({ title: '', text: '', audience: 'PARTICIPANTS' });
      setAnnouncementOpen(false);
      await load(true);
      setSelected(conversation);
      setError('');
    } catch (e) {
      setError('The announcement could not be published.');
    } finally {
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (!group.title.trim() || (!group.audience && group.schoolIds.length < 2) || busy) return;
    setBusy(true);
    try {
      const conversation = await createTournamentGroupConversation(user, tournamentId, {
        title: group.title.trim(),
        ...(group.audience ? { audience: group.audience } : { schoolIds: group.schoolIds })
      });
      setGroup({ title: '', audience: '', schoolIds: [] });
      setGroupOpen(false);
      await load(true);
      setSelected({ ...conversation, canWrite: true, canManage: true });
      setError('');
    } catch (e) {
      setError('The group chat could not be created. Select at least two active schools.');
    } finally {
      setBusy(false);
    }
  };

  const mentionInFollowUp = (person: any) => {
    const label = String(person.label || person.name || '').trim();
    if (!label) return;
    setText(value => `${value.trim()}${value.trim() ? ' ' : ''}@${label} `);
    setMentions(values => values.some(item => String(item.userId) === String(person.userId) && String(item.permissionId || '') === String(person.permissionId || '')) ? values : [...values, {...person, label}]);
    setExpandedReceiptMessageId('');
    window.setTimeout(() => composerRef.current && composerRef.current.focus(), 0);
  };

  const mentionQuery = () => {
    const match = text.match(/(?:^|\s)@([^@\s]*)$/);
    return match ? match[1].toLowerCase() : undefined;
  };
  const visibleMentionCandidates = Array.from(new Map(mentionCandidates.map(candidate => [`${candidate.userId}:${candidate.schoolId || candidate.permissionId}`, candidate])).values())
    .filter((candidate: any) => mentionQuery() === undefined || String(candidate.label || '').toLowerCase().includes(mentionQuery()))
    .slice(0, 8);
  const addMention = (candidate: any) => {
    const label = String(candidate.label || '').trim();
    if (!label) return;
    setText(value => {
      const match = value.match(/(?:^|\s)@([^@\s]*)$/);
      if (!match || match.index === undefined) return `${value}${value && !value.endsWith(' ') ? ' ' : ''}@${label} `;
      const atIndex = match.index + match[0].indexOf('@');
      return `${value.slice(0, atIndex)}@${label} `;
    });
    setMentions(values => values.some(item => String(item.userId) === String(candidate.userId)) ? values : [...values, {...candidate, label}]);
    setMentionOpen(false);
    window.setTimeout(() => composerRef.current && composerRef.current.focus(), 0);
  };

  const renderMessageText = (message: any) => {
    const value = String(message.text || '');
    const messageMentions = (message.mentions || []).filter(item => item.label);
    if (!messageMentions.length) return value;
    const byLabel = new Map(messageMentions.map(item => [String(item.label).toLowerCase(), item]));
    const pattern = messageMentions.map(item => String(item.label).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length).join('|');
    return value.split(new RegExp(`(@(?:${pattern}))`, 'gi')).map((part, index) => {
      const mention = part.charAt(0) === '@' ? byLabel.get(part.slice(1).toLowerCase()) : undefined;
      return mention
        ? <button key={`${part}:${index}`} type="button" className="utmComms__inlineMention" title="Mention in follow-up" onClick={() => mentionInFollowUp(mention)}>{part}</button>
        : <React.Fragment key={`${part}:${index}`}>{part}</React.Fragment>;
    });
  };

  if (loading) return <div className="utmComms__loading">Loading tournament chats…</div>;

  return (
    <section className={`utmComms ${threadOnly ? 'utmComms--schoolThread' : ''}`}>
      {!settings.enabled ? (
        <div className="utmComms__disabled">
          <strong>
            {settings.platformAvailable === false
              ? `Tournament chats are not enabled for ${settings.ownerName || 'the coordinating union'}`
              : 'Tournament chats are turned off'}
          </strong>
          <span>
            {settings.platformAvailable === false
              ? 'Contact Squad In Touch support to enable secure tournament communications. Existing safeguarding history remains available as read-only.'
              : 'Existing conversations remain available as read-only safeguarding history. An organiser can enable chats in Settings → Communications.'}
          </span>
        </div>
      ) : null}
      {settings.enabled && !organiserMode && !settings.canInitiate ? (
        <div className="utmComms__disabled">
          <strong>Your school cannot start a new tournament chat</strong>
          <span>The organiser’s communication settings only allow eligible schools to initiate conversations. You can still read and reply to existing open chats.</span>
        </div>
      ) : null}
      {error ? (
        <div className="utmComms__error" role="alert">
          {error}
        </div>
      ) : null}
      {privateOpen ? (
        <div
          className="utmComms__modalBackdrop"
          role="presentation"
          onMouseDown={event => event.target === event.currentTarget && setPrivateOpen(false)}
        >
          <section
            className="utmComms__createModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-private-chat-title"
          >
            <header>
              <div>
                <h3 id="new-private-chat-title">New private chat</h3>
                <p>Talk to one school. All of its staff linked to this tournament can read and reply.</p>
              </div>
              <button type="button" aria-label="Close" onClick={() => setPrivateOpen(false)}>
                ×
              </button>
            </header>
            <div className="utmComms__modalBody">
              <input className="utmComms__schoolSearch" placeholder="Search schools" aria-label="Search schools" />
              <div className="utmComms__schoolPicker">
                {audiences
                  .filter(row => row.relationship !== 'HISTORICAL')
                  .map(row => {
                    const id = String(row.school.id || row.school._id);
                    return (
                      <label key={id} className={schoolId === id ? 'isSelected' : ''}>
                        <input
                          type="radio"
                          name="private-school"
                          checked={schoolId === id}
                          onChange={() => setSchoolId(id)}
                        />
                        <span className="utmComms__schoolAvatar">
                          {String(row.school.name || '')
                            .split(/\s+/)
                            .slice(0, 2)
                            .map(word => word[0])
                            .join('')}
                        </span>
                        <span>
                          <strong>{row.school.name}</strong>
                          <small>
                            {row.staffCount || 0} staff ·{' '}
                            {String(row.relationship || '')
                              .toLowerCase()
                              .replace('_', ' ')}
                          </small>
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
            <footer>
              <span>{schoolId ? '1 school' : 'Choose a school'}</span>
              <button type="button" onClick={() => setPrivateOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="isPrimary"
                disabled={!schoolId || busy}
                onClick={async () => {
                  await openSchoolChat();
                  setPrivateOpen(false);
                }}
              >
                Start chat
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      {announcementOpen ? (
        <div className="utmComms__composer">
          <header>
            <div>
              <h3>New announcement</h3>
              <p>A read-only post. Schools can’t reply.</p>
            </div>
            <button type="button" aria-label="Close" onClick={() => setAnnouncementOpen(false)}>
              ×
            </button>
          </header>
          <div>
            <label>
              Audience
              <select
                value={announcement.audience}
                onChange={event => setAnnouncement({ ...announcement, audience: event.target.value })}
              >
                {settings.announcementAudiences.includes('PARTICIPANTS') ? (
                  <option value="PARTICIPANTS">Confirmed participants</option>
                ) : null}
                {settings.announcementAudiences.includes('WAITING_LIST') ? (
                  <option value="WAITING_LIST">Waiting list</option>
                ) : null}
                {settings.announcementAudiences.includes('INVITED') ? (
                  <option value="INVITED">Invited schools</option>
                ) : null}
                {settings.announcementAudiences.includes('ALL_ENGAGED') ? (
                  <option value="ALL_ENGAGED">All connected schools</option>
                ) : null}
              </select>
            </label>
            <label>
              Title
              <input
                value={announcement.title}
                onChange={event => setAnnouncement({ ...announcement, title: event.target.value })}
                placeholder="Tournament update"
              />
            </label>
          </div>
          <textarea
            value={announcement.text}
            onChange={event => setAnnouncement({ ...announcement, text: event.target.value })}
            placeholder="Write the announcement…"
          />
          <footer>
            <button type="button" onClick={() => setAnnouncementOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="isPrimary"
              disabled={busy || !announcement.text.trim() || !allowedAnnouncementAudiences.includes(announcement.audience)}
              onClick={publish}
            >
              Publish announcement
            </button>
          </footer>
        </div>
      ) : null}
      {groupOpen ? (
        <div className="utmComms__composer">
          <header>
            <div>
              <h3>New group chat</h3>
              <p>A discussion with several schools. You can add or remove schools later.</p>
            </div>
            <button type="button" aria-label="Close" onClick={() => setGroupOpen(false)}>
              ×
            </button>
          </header>
          <div>
            <label>
              Chat name
              <input
                value={group.title}
                onChange={event => setGroup({ ...group, title: event.target.value })}
                placeholder="For example: Team managers"
              />
            </label>
            <label>
              Quick select
              <select
                value={group.audience}
                onChange={event => setGroup({ ...group, audience: event.target.value, schoolIds: [] })}
              >
                <option value="">Choose schools individually</option>
                <option value="PARTICIPANTS">Confirmed participants</option>
                <option value="WAITING_LIST">Waiting list</option>
                <option value="INVITED">Invited schools</option>
                <option value="ALL_ENGAGED">All connected schools</option>
              </select>
            </label>
          </div>
          {!group.audience ? (
            <div className="utmComms__groupSchools">
              {audiences
                .filter(row => row.relationship !== 'HISTORICAL')
                .map(row => {
                  const id = String(row.school.id || row.school._id);
                  const checked = group.schoolIds.includes(id);
                  return (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setGroup({
                            ...group,
                            schoolIds: checked
                              ? group.schoolIds.filter(value => value !== id)
                              : [...group.schoolIds, id]
                          })
                        }
                      />
                      <span>{row.school.name}</span>
                      <small>
                        {String(row.relationship)
                          .toLowerCase()
                          .replace('_', ' ')}
                      </small>
                    </label>
                  );
                })}
            </div>
          ) : (
            <p>Every school in this audience will see other participants and can reply.</p>
          )}
          <footer>
            <button type="button" onClick={() => setGroupOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="isPrimary"
              disabled={busy || !group.title.trim() || (!group.audience && group.schoolIds.length < 2)}
              onClick={createGroup}
            >
              Create group chat
            </button>
          </footer>
        </div>
      ) : null}
      <div className={`utmComms__workspace ${detailsOpen ? 'hasDetails' : ''} ${threadOnly ? 'isThreadOnly' : ''}`}>
        <aside className="utmComms__inbox" aria-label="Tournament conversations">
          <div className="utmComms__inboxHeader">
            <h2>Chats</h2>
            {canCreateConversation ? (
              <div className="utmComms__newMenu" ref={newMenuRef}>
                <button type="button" onClick={() => setNewMenuOpen(value => !value)}>
                  <span>New</span>
                  <span className="utmComms__newChevron" aria-hidden="true">
                    ⌄
                  </span>
                </button>
                {newMenuOpen ? (
                  <div>
                    {organiserMode ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setNewMenuOpen(false);
                            setPrivateOpen(true);
                            setGroupOpen(false);
                            setAnnouncementOpen(false);
                            setSchoolId('');
                          }}
                        >
                          Private chat<small>One school and your organiser team</small>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNewMenuOpen(false);
                            setGroupOpen(true);
                            setAnnouncementOpen(false);
                          }}
                        >
                          Group chat<small>Several schools in one discussion</small>
                        </button>
                        {allowedAnnouncementAudiences.length ? (
                          <button
                            type="button"
                            onClick={() => {
                              setNewMenuOpen(false);
                              setAnnouncement({...announcement, audience: allowedAnnouncementAudiences[0]});
                              setAnnouncementOpen(true);
                              setGroupOpen(false);
                            }}
                          >
                            Announcement<small>Read-only post</small>
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={!settings.canInitiate}
                        onClick={() => {
                          setNewMenuOpen(false);
                          openSchoolChat();
                        }}
                      >
                        Private chat<small>Message the tournament organiser</small>
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="utmComms__search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4" /></svg>
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              type="search"
              placeholder="Search conversations"
              aria-label="Search conversations"
            />
          </div>
          <div className="utmComms__filters">
            {[
              ['ALL', 'All', 0],
              ['UNREAD', 'Unread', unreadCount],
              ['ASSIGNED', 'Assigned to me', assignedCount],
              ['CLOSED', 'Closed & archived', closedCount]
            ].map(([value, label, count]: any) => (
              <button
                type="button"
                key={value}
                className={inboxFilter === value ? 'isActive' : ''}
                aria-pressed={inboxFilter === value}
                onClick={() => setInboxFilter(value)}
              >
                {label}
                {count ? <b className="utmComms__filterCount">{count}</b> : null}
              </button>
            ))}
            <label className="utmComms__typeFilter"><span>Conversation type</span><select aria-label="Chat type" value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
              <option value="ALL">All types</option>
              <option value="PRIVATE">Private chats</option>
              <option value="GROUP">Group chats</option>
              <option value="ANNOUNCEMENT">Announcements</option>
            </select></label>
          </div>
          <div className="utmComms__list">
            {visibleConversations.map(item => (
              <button
                type="button"
                key={idOf(item)}
                className={selected && idOf(item) === idOf(selected) ? 'isActive' : ''}
                onClick={() => setSelected(item)}
              >
                <span>
                  {item.type === 'ANNOUNCEMENT'
                    ? 'Read-only announcement'
                    : item.type === 'GROUP'
                    ? 'Group chat'
                    : 'Private chat'}
                  {item.unread ? <i>New</i> : null}
                </span>
                <strong title={conversationName(item)}>{conversationName(item)}</strong>
                <small>
                  {item.lastMessagePreview || 'No messages yet'}
                  {item.lastMessageAt ? ` · ${moment(item.lastMessageAt).fromNow()}` : ''}
                  {item.type === 'ANNOUNCEMENT'
                    ? ` · ${item.createdByName || 'Tournament organiser'} · ${moment(item.createdAt).format(
                        'D MMM, HH:mm'
                      )}`
                    : ''}
                </small>
              </button>
            ))}
            {!visibleConversations.length ? <div className="utmComms__inboxEmpty" role="status">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4zM7 8h10M7 12h6" /></svg>
              <strong>{search || inboxFilter !== 'ALL' || typeFilter !== 'ALL' ? 'No matching conversations' : 'No conversations yet'}</strong>
              <p>{search || inboxFilter !== 'ALL' || typeFilter !== 'ALL' ? 'Try another filter or clear the search.' : 'Tournament messages and announcements will appear here.'}</p>
            </div> : null}
          </div>
          <footer className="utmComms__inboxFooter">
            {conversations.length} chats{closedCount ? ` · ${closedCount} closed or archived hidden` : ''}
          </footer>
        </aside>
        <main>
          {selected ? (
            <>
              <header>
                <div>
                  <span>
                    {!organiserMode
                      ? selected.type === 'ANNOUNCEMENT'
                        ? `Announcement · ${displayText(tournament.name, 'Tournament')}`
                        : selected.type === 'GROUP'
                        ? `${displayText(tournament.name, 'Tournament')} · Group: ${(selected.audienceSchoolIds || selected.schoolIds || []).length} schools`
                        : `${displayText(tournament.name, 'Tournament')} · Private: ${displayText(selected.organiserName, 'organisers')} and your school`
                      : selected.type === 'ANNOUNCEMENT'
                      ? 'Read-only announcement'
                      : selected.type === 'GROUP'
                      ? 'Group chat'
                      : 'Private school chat'}
                  </span>
                  {editingTitle ? (
                    <div className="utmComms__titleEditor">
                      <input autoFocus value={title} maxLength={120} onChange={event => setTitle(event.target.value)} />
                      <button type="button" disabled={busy} onClick={saveTitle}>
                        Save
                      </button>
                      <button type="button" onClick={() => setEditingTitle(false)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <h3>{conversationName(selected)}</h3>
                  )}
                </div>
                <div className="utmComms__chatActions">
                  {settings.enabled && selected.canManage && ['PRIVATE', 'GROUP'].includes(selected.type) ? (
                    <button
                      type="button"
                      className={String(selected.assignedToUserId) === String(user.userId) ? 'isAssigned' : ''}
                      onClick={assignToMe}
                    >
                      {String(selected.assignedToUserId) === String(user.userId) ? 'Assigned to you' : 'Assign to me'}
                    </button>
                  ) : null}
                  {settings.enabled && selected.canManage && ['PRIVATE', 'GROUP'].includes(selected.type) ? (
                    <div className="utmComms__headerMenu" ref={statusMenuRef}>
                      <button type="button" onClick={() => setStatusMenuOpen(value => !value)}>
                        <i className={`utmComms__statusDot is${selected.status || 'OPEN'}`} />
                        {selected.status === 'READ_ONLY'
                          ? 'Announcements only'
                          : selected.status === 'ARCHIVED'
                          ? 'Archived'
                          : selected.status === 'CLOSED'
                          ? 'Closed'
                          : 'Open'}
                        <span className="utmComms__menuChevron" aria-hidden="true">
                          ⌄
                        </span>
                      </button>
                      {statusMenuOpen ? (
                        <div className="utmComms__statusMenu">
                          {[
                            ['OPEN', 'Open', 'Everyone in the chat can write.'],
                            ['READ_ONLY', 'Announcements only', 'Only organisers can write. Schools can read.'],
                            ['CLOSED', 'Closed', 'Read-only for everyone.'],
                            ['ARCHIVED', 'Archive', 'Hidden from the list. You can restore it.']
                          ].filter(([value]) => !settings.autoClosed || !['OPEN', 'READ_ONLY'].includes(value)).map(([value, label, help]) => (
                            <button
                              type="button"
                              key={value}
                              onClick={() => {
                                setStatusMenuOpen(false);
                                updateStatus(value);
                              }}
                            >
                              <i className={`utmComms__statusDot is${value}`} />
                              <span>
                                <strong>{label}</strong>
                                <small>{help}</small>
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : settings.enabled && selected.canManage && selected.type === 'ANNOUNCEMENT' ? (
                    <button type="button">Edit</button>
                  ) : selected.status !== 'OPEN' ? (
                    <em>
                      {selected.status === 'CLOSED' ? 'Closed' : 'Organisers only'}
                    </em>
                  ) : null}
                  <div className="utmComms__headerMenu">
                    <button
                      type="button"
                      onClick={() => setMoreMenuOpen(value => !value)}
                      aria-label="More conversation actions"
                    >
                      ⋯
                    </button>
                    {moreMenuOpen ? (
                      <div className="utmComms__moreMenu">
                        <button
                          type="button"
                          onClick={() => {
                            setMoreMenuOpen(false);
                            setDetailsOpen(true);
                          }}
                        >
                          Show details
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMoreMenuOpen(false);
                            setMessageSearchOpen(true);
                          }}
                        >
                          Search in this chat
                        </button>
                        {settings.enabled && selected.canManage && selected.type !== 'ANNOUNCEMENT' ? (
                          <button
                            type="button"
                            onClick={() => {
                              setMoreMenuOpen(false);
                              setTitle(selected.title || '');
                              setEditingTitle(true);
                            }}
                          >
                            Rename
                          </button>
                        ) : null}
                        {selected.canManage ? (
                          <>
                            <hr />
                            <button
                              type="button"
                              onClick={() => {
                                setMoreMenuOpen(false);
                                showAudit();
                              }}
                            >
                              Audit log
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setMoreMenuOpen(false);
                                downloadTournamentSafeguardingReport(user, tournamentId, idOf(selected)).catch(() =>
                                  setError('The safeguarding PDF could not be generated.')
                                );
                              }}
                            >
                              Download safeguarding PDF
                            </button>
                          </>
                        ) : null}
                        {settings.enabled && selected.canManage && selected.type === 'ANNOUNCEMENT' ? (
                          <>
                            <hr />
                            <button
                              type="button"
                              className="isDanger"
                              onClick={async () => {
                                setMoreMenuOpen(false);
                                if (
                                  !window.confirm(`Delete “${selected.title || 'this announcement'}” for all schools?`)
                                ) {
                                  return;
                                }
                                setBusy(true);
                                try {
                                  await deleteTournamentAnnouncement(user, tournamentId, idOf(selected));
                                  setSelected(undefined);
                                  await load(true);
                                } catch (e) {
                                  setError('The announcement could not be deleted.');
                                } finally {
                                  setBusy(false);
                                }
                              }}
                            >
                              Delete announcement
                            </button>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </header>
              {messageSearchOpen ? (
                <div className="utmComms__messageSearch">
                  <input
                    value={messageSearch}
                    onChange={event => setMessageSearch(event.target.value)}
                    placeholder="Search this conversation"
                    aria-label="Search messages"
                  />
                  <span>{messages.length} found</span>
                  <button
                    type="button"
                    onClick={() => {
                      setMessageSearchOpen(false);
                      setMessageSearch('');
                    }}
                  >
                    Done
                  </button>
                </div>
              ) : null}
              <div className="utmComms__messages" ref={messagesRef}>
                {selected.type === 'ANNOUNCEMENT' ? (
                  <article className="utmComms__announcementCard">
                    <small>
                      Announcement · {String(selected.audience || 'All connected schools').replace(/_/g, ' ')}
                    </small>
                    <h3>{conversationName(selected)}</h3>
                    <p>{displayText(selected.text || selected.lastMessagePreview)}</p>
                    <footer>
                      Published by {displayText(selected.createdByName, 'Tournament organiser')} ·{' '}
                      {moment(selected.createdAt).format('D MMM, HH:mm')}
                      {selected.editedAt ? ` · Edited ${moment(selected.editedAt).format('D MMM, HH:mm')}` : ''}
                      {selected.receiptSummary
                        ? ` · Read by ${selected.receiptSummary.readCount || 0} of ${selected.receiptSummary
                            .totalCount || 0} schools`
                        : ''}
                    </footer>
                  </article>
                ) : null}
                {messages.map(message => {
                  const quoted = message.replyToMessageId
                    ? messages.find(item => idOf(item) === String(message.replyToMessageId))
                    : undefined;
                  const authorName = displayText(message.authorDisplay && message.authorDisplay.name, displayText(message.author && message.author.fullName, 'Staff'));
                  const avatar = message.author && message.author.avatar;
                  const mine = String(message.authorUserId) === String(user.userId);
                  return (
                    <div key={message.id || message._id} className={`utmComms__messageRow ${mine ? 'isMine' : ''}`}>
                    <div className={`utmComms__messageAvatar ${avatar ? 'hasImage' : ''}`} aria-hidden="true">
                      {avatar ? <img src={avatar} alt="" /> : initials(authorName)}
                    </div>
                    <article className={mine ? 'isMine' : ''}>
                      <div>
                        <strong>{authorName}</strong>
                        {!mine ? <span>{displayText(message.authorDisplay && message.authorDisplay.roleLabel)}</span> : null}
                        <time>{moment(message.createdAt).format('DD MMM, HH:mm')}</time>
                      </div>
                      {quoted ? (
                        <blockquote>
                          <strong>{displayText(quoted.authorDisplay && quoted.authorDisplay.name, 'Staff')}</strong>
                          <span>{displayText(quoted.text)}</span>
                        </blockquote>
                      ) : null}
                      {message.reportSummary ? (
                        <div className="utmComms__reported">
                          <strong>Reported for organiser review</strong>
                          <span>
                            {message.reportSummary.count} open report{message.reportSummary.count === 1 ? '' : 's'}
                          </span>
                          <div>
                            <button type="button" disabled={busy} onClick={() => resolveReport(message, 'KEEP')}>
                              Keep message
                            </button>
                            <button type="button" disabled={busy} onClick={() => resolveReport(message, 'REMOVE')}>
                              Remove message
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <p>{message.deletedAt ? 'Message removed' : renderMessageText(message)}</p>
                      {String(message.authorUserId) === String(user.userId) && message.receiptSummary && (organiserMode || selected.type === 'PRIVATE') ? (
                        <div className="utmComms__receipts">
                          <button type="button" disabled={!((message.receiptSummary.readers || []).length || (message.receiptSummary.unreadReaders || []).length)} onClick={() => setExpandedReceiptMessageId(expandedReceiptMessageId === idOf(message) ? '' : idOf(message))}>
                            {!organiserMode ? (message.receiptSummary.readCount ? 'Read by organiser' : 'Not read yet') : message.receiptSummary.readCount ? `Read by ${message.receiptSummary.readCount}${message.receiptSummary.unreadCount ? ` · ${message.receiptSummary.unreadCount} not read` : ''}` : message.receiptSummary.unreadCount ? `Not read by ${message.receiptSummary.unreadCount}` : 'Not read yet'}
                          </button>
                          {message.receiptSummary.privacyProtectedCount > 0 ? <span> · Some read statuses are private</span> : null}
                          {expandedReceiptMessageId === idOf(message) && ((message.receiptSummary.readers || []).length || (message.receiptSummary.unreadReaders || []).length) ? <div className="utmComms__receiptDetails">
                            {message.receiptSummary.readers && message.receiptSummary.readers.length ? <h5>Read</h5> : null}
                            {(message.receiptSummary.readers || []).map(reader => <div key={`read:${reader.userId}:${displayText(reader.roleLabel)}`}><b>{displayText(reader.name, 'Unknown user')}</b><span>{displayText(reader.roleLabel)}{reader.readAt ? ` · ${new Date(reader.readAt).toLocaleString()}` : ''}</span></div>)}
                            {message.receiptSummary.unreadReaders && message.receiptSummary.unreadReaders.length ? <h5>Not read yet</h5> : null}
                            {(message.receiptSummary.unreadReaders || []).map(reader => <div key={`unread:${reader.userId}:${displayText(reader.roleLabel)}`}><b>{displayText(reader.name, 'Unknown user')}<small>{displayText(reader.roleLabel)}</small></b><button type="button" onClick={() => mentionInFollowUp(reader)}>Mention in follow-up</button></div>)}
                          </div> : null}
                        </div>
                      ) : null}
                      {selected.canWrite || !settings.enabled ? (
                        <div className="utmComms__messageActions">
                          {selected.canWrite ? <button type="button" className="utmComms__reply" onClick={() => setReplyTo(message)}>
                            Reply
                          </button> : null}
                          {selected.canWrite && String(message.authorUserId) === String(user.userId) && !message.deletedAt ? (
                            <button
                              type="button"
                              onClick={() => {
                                setActionMessage(message);
                                setAction('EDIT');
                                setActionValue(message.text);
                              }}
                            >
                              Edit
                            </button>
                          ) : null}
                          {!message.deletedAt && (selected.canWrite || selected.canManage || String(message.authorUserId) !== String(user.userId)) ? (
                            <button
                              type="button"
                              onClick={() => {
                                setActionMessage(message);
                                setAction(
                                  (selected.canWrite && String(message.authorUserId) === String(user.userId)) || selected.canManage
                                    ? 'DELETE'
                                    : 'REPORT'
                                );
                                setActionValue('');
                              }}
                            >
                              {(selected.canWrite && String(message.authorUserId) === String(user.userId)) || selected.canManage
                                ? 'Remove'
                                : message.viewerReported
                                ? 'Reported'
                                : 'Report'}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </article>
                    </div>
                  );
                })}
              </div>
              {selected.canWrite ? (
                <div className="utmComms__send">
                  {replyTo ? (
                    <div className="utmComms__replying">
                      <span>
                        Replying to <strong>{(replyTo.authorDisplay && replyTo.authorDisplay.name) || 'Staff'}</strong>:{' '}
                        {replyTo.text}
                      </span>
                      <button type="button" aria-label="Cancel reply" onClick={() => setReplyTo(undefined)}>
                        ×
                      </button>
                    </div>
                  ) : null}
                  <textarea
                    ref={composerRef}
                    value={text}
                    onChange={event => {
                      const value = event.target.value;
                      setText(value);
                      setMentions(values => values.filter(item => value.includes(`@${item.label}`)));
                      setMentionOpen(/(?:^|\s)@[^@\s]*$/.test(value));
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        send();
                      }
                    }}
                    placeholder={!organiserMode ? selected.type === 'GROUP' ? `Message ${(selected.audienceSchoolIds || selected.schoolIds || []).length} schools and organisers · @ to mention` : `Message ${displayText(selected.organiserName, 'tournament')} organisers · @ to mention` : 'Write a message…'}
                  />
                  <div className="utmComms__mentionControl" ref={mentionControlRef}>
                    <button type="button" aria-label="Mention someone" title="Mention someone" onClick={() => {
                      if (!mentionOpen && mentionQuery() === undefined) setText(value => `${value}${value && !value.endsWith(' ') ? ' ' : ''}@`);
                      setMentionOpen(value => !value);
                      window.setTimeout(() => composerRef.current && composerRef.current.focus(), 0);
                    }}>
                      @ Mention staff
                    </button>
                    {mentionOpen ? (
                      <div className="utmComms__mentionPicker">
                        <header>Mention someone</header>
                        {visibleMentionCandidates.map((candidate: any) => (
                          <button
                            type="button"
                            key={`${candidate.userId}:${candidate.permissionId}`}
                            onClick={() => addMention(candidate)}
                          >
                            <strong>{displayText(candidate.label, 'Staff member')}</strong>
                            <small>{displayText(candidate.roleLabel)}</small>
                          </button>
                        ))}
                        {!visibleMentionCandidates.length ? <p>No matching people</p> : null}
                      </div>
                    ) : null}
                  </div>
                  <button type="button" disabled={busy || !text.trim()} onClick={send}>
                    Send
                  </button>
                </div>
              ) : (
                <div className="utmComms__readOnly">
                  {selected.type === 'ANNOUNCEMENT'
                    ? 'Announcements can’t be replied to. Questions go to the organisers privately.'
                    : selected.status === 'CLOSED'
                    ? 'The organiser closed this chat. You can still read it.'
                    : 'Only organisers can post here. You’ll be notified when they do.'}
                  {!organiserMode && selected.type !== 'PRIVATE' ? <button type="button" onClick={openSchoolChat}>{selected.type === 'ANNOUNCEMENT' ? 'Ask the organiser' : 'Message organiser privately'}</button> : null}
                </div>
              )}
            </>
          ) : (
            <div className="utmComms__empty">
              <strong>Select a conversation</strong>
              <span>Choose a school chat or announcement from the list.</span>
            </div>
          )}
        </main>
        {selected ? (
          <aside className={`utmComms__details ${detailsOpen ? 'isOpen' : ''}`}>
            <button
              type="button"
              className="utmComms__detailsClose"
              aria-label="Close details"
              onClick={() => setDetailsOpen(false)}
            >
              ×
            </button>
            {organiserMode ? (
              <section>
                <h4>
                  Assigned to <small>Only organisers see this</small>
                </h4>
                <div className="utmComms__detailPerson">
                  <span>
                    {String(selected.assignedToName || 'N')
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  <div>
                    <strong>
                      {String(selected.assignedToUserId) === String(user.userId)
                        ? 'You'
                        : selected.assignedToName || 'Nobody yet'}
                    </strong>
                    <button type="button" onClick={assignToMe}>
                      {String(selected.assignedToUserId) === String(user.userId) ? 'Unassign me' : 'Assign to me'}
                    </button>
                  </div>
                </div>
              </section>
            ) : null}
            {!organiserMode ? <>
              <section className="utmComms__tournamentDetails">
                <h4>Tournament</h4>
                <strong>{displayText(tournament.name, 'Tournament')}</strong>
                {tournament.startTime ? <p>{moment(tournament.startTime).format('D MMM YYYY, HH:mm')}</p> : null}
                {(tournament as any).venue ? <small>{displayText((tournament as any).venue.name || (tournament as any).venue)}</small> : null}
                <button type="button" onClick={() => window.location.assign(`/tournaments/upcoming`)}>Open tournament</button>
              </section>
              <section>
                <h4>Organisers</h4>
                <div className="utmComms__detailPerson">
                  <span>{initials(selected.organiserName)}</span>
                  <div><strong>{displayText(selected.organiserName, 'Tournament organisers')}</strong><small>Organiser team</small></div>
                </div>
              </section>
            </> : null}
            <section>
              <h4>Status</h4>
              <p>
                <i className={`utmComms__statusDot is${selected.status || 'OPEN'}`} />
                {selected.status === 'READ_ONLY'
                  ? 'Announcements only'
                  : String(selected.status || 'OPEN')
                      .toLowerCase()
                      .replace(/^./, value => value.toUpperCase())}
              </p>
              <small>
                {selected.status === 'OPEN'
                  ? 'Everyone in the chat can write.'
                  : selected.status === 'READ_ONLY'
                  ? 'Only organisers can write. Schools can read.'
                  : 'This chat is read-only.'}
              </small>
            </section>
            <section>
              <h4>
                {selected.type === 'GROUP'
                  ? `Schools ${selected.schoolIds ? selected.schoolIds.length : ''}`
                  : 'School'}
              </h4>
              {(selected.schoolIds || [selected.counterpartySchoolId]).filter(Boolean).map(id => (
                <div className="utmComms__detailSchool" key={String(id)}>
                  <span className="utmComms__schoolAvatar">
                    {String(schoolNames.get(String(id)) || 'S')
                      .split(/\s+/)
                      .slice(0, 2)
                      .map(word => word[0])
                      .join('')}
                  </span>
                  <div>
                    <strong>{schoolNames.get(String(id)) || selected.counterpartySchoolName || 'School'}</strong>
                    <small>Staff linked to this tournament</small>
                  </div>
                </div>
              ))}
            </section>
            <section>
              <h4>Started by</h4>
              <p>
                {selected.createdByName || selected.organiserName || 'Tournament organiser'} ·{' '}
                {selected.createdAt ? moment(selected.createdAt).format('D MMM YYYY') : ''}
              </p>
            </section>
          </aside>
        ) : null}
      </div>
      {action && actionMessage ? (
        <div className="utmComms__modalBackdrop" role="presentation">
          <section
            className="utmComms__actionModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tournament-message-action"
          >
            <h3 id="tournament-message-action">
              {action === 'EDIT' ? 'Edit message' : action === 'REPORT' ? 'Report message' : 'Remove message'}
            </h3>
            <p>
              {action === 'REPORT'
                ? 'The tournament organiser will review your concern. Reports are recorded in the safeguarding audit.'
                : action === 'DELETE'
                ? 'The original message remains preserved in the safeguarding audit.'
                : 'Update the message text below.'}
            </p>
            {action === 'EDIT' ? (
              <textarea autoFocus value={actionValue} onChange={event => setActionValue(event.target.value)} />
            ) : (
              <textarea
                autoFocus
                value={actionValue}
                onChange={event => setActionValue(event.target.value)}
                placeholder={action === 'REPORT' ? 'Explain your concern' : 'Reason for removal'}
              />
            )}
            <footer>
              <button
                type="button"
                onClick={() => {
                  setAction(undefined);
                  setActionMessage(undefined);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={action === 'EDIT' ? 'isPrimary' : 'isDanger'}
                disabled={busy || !actionValue.trim()}
                onClick={runMessageAction}
              >
                {action === 'EDIT' ? 'Save changes' : action === 'REPORT' ? 'Submit report' : 'Remove message'}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      {auditOpen ? (
        <div className="utmComms__modalBackdrop" role="presentation">
          <section
            className="utmComms__actionModal utmComms__auditModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tournament-audit-title"
          >
            <h3 id="tournament-audit-title">Conversation audit</h3>
            <p>Safeguarding actions, status changes and message moderation are preserved here.</p>
            <div>
              {audit.map(item => (
                <article key={idOf(item)}>
                  <strong>
                    {String(item.action || '')
                      .toLowerCase()
                      .replace(/_/g, ' ')
                      .replace(/^./, value => value.toUpperCase())}
                  </strong>
                  <time>{moment(item.createdAt).format('DD MMM YYYY, HH:mm')}</time>
                  {item.reason ? <p>{displayText(item.reason)}</p> : null}
                </article>
              ))}
            </div>
            <footer>
              <button type="button" className="isPrimary" onClick={() => setAuditOpen(false)}>
                Close
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
};
